import React from 'react';
import { Clock, Umbrella, CheckCircle, HourglassSimple, XCircle } from '@phosphor-icons/react';
import { format, parseISO } from 'date-fns';
import { getLeaveTypeStyle, getLeaveTypeLabel } from '../../utils/styleHelpers';

export const StatsHoursWidget = ({ wrapStyle, stats, language }) => (
  <div style={wrapStyle}>
    <div className="stats-grid">
      <div className="stat-card" data-testid="stat-weekly-hours">
        <div className="stat-label">{language === 'en' ? 'Hours this week' : 'Heures cette semaine'}</div>
        <div className="stat-value">{stats.weeklyHours}h</div>
      </div>
      <div className="stat-card" data-testid="stat-monthly-hours">
        <div className="stat-label">{language === 'en' ? 'Hours this month' : 'Heures ce mois'}</div>
        <div className="stat-value">{stats.monthlyHours}h</div>
      </div>
      <div className="stat-card" data-testid="stat-pending">
        <div className="stat-label">{language === 'en' ? 'Pending' : 'En attente'}</div>
        <div className="stat-value">{stats.pendingEntries}</div>
      </div>
      <div className="stat-card" data-testid="stat-approved">
        <div className="stat-label">{language === 'en' ? 'Approved' : 'Validees'}</div>
        <div className="stat-value">{stats.approvedEntries}</div>
      </div>
    </div>
  </div>
);

export const LeaveBalanceWidget = ({ wrapStyle, leaveBalance, isAdmin, language }) => {
  if (!leaveBalance) return null;
  return (
    <div style={wrapStyle}>
      <div className="stats-grid" style={{ gridTemplateColumns: '1fr' }}>
        <div className="stat-card" data-testid="stat-leave-balance">
          <div className="stat-label" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <Umbrella size={16} />
            {isAdmin
              ? (language === 'en' ? 'Team leave remaining' : 'Conges restants (equipe)')
              : (language === 'en' ? 'Leave remaining' : 'Conges restants')}
          </div>
          <div className="stat-value" style={{ color: leaveBalance.remaining > 0 ? '#2E7D32' : '#C62828' }}>
            {isAdmin ? `${leaveBalance.remaining}j` : `${leaveBalance.remaining}/${leaveBalance.total}j`}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AbsencesWidget = ({ wrapStyle, absencesCalendar, language }) => {
  if (!absencesCalendar.length) return null;
  return (
    <div style={wrapStyle}>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Umbrella size={18} /> {language === 'en' ? 'Absences this month' : 'Absences ce mois'}
          </h3>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table" data-testid="absences-calendar-table">
            <thead><tr><th>{language === 'en' ? 'Employee' : 'Salarie'}</th><th>Type</th><th>{language === 'en' ? 'From' : 'Du'}</th><th>{language === 'en' ? 'To' : 'Au'}</th><th>{language === 'en' ? 'Days' : 'Jours'}</th></tr></thead>
            <tbody>
              {absencesCalendar.map(a => (
                <tr key={a.id}>
                  <td style={{ fontWeight: '600' }}>{a.user_name}</td>
                  <td><span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.75rem', fontWeight: '600', backgroundColor: getLeaveTypeStyle(a.type).bg, color: getLeaveTypeStyle(a.type).color }}>{getLeaveTypeLabel(a.type)}</span></td>
                  <td>{a.start_date}</td><td>{a.end_date}</td>
                  <td style={{ fontWeight: '700' }}>{a.days}j</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export const RecentEntriesWidget = ({ wrapStyle, recentEntries, language }) => {
  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved': return <span className="status-badge approved"><CheckCircle size={14} /> {language === 'en' ? 'Approved' : 'Valide'}</span>;
      case 'rejected': return <span className="status-badge rejected"><XCircle size={14} /> {language === 'en' ? 'Rejected' : 'Rejete'}</span>;
      default: return <span className="status-badge pending"><HourglassSimple size={14} /> {language === 'en' ? 'Pending' : 'En attente'}</span>;
    }
  };

  return (
    <div style={wrapStyle}>
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">{language === 'en' ? 'Recent entries' : 'Entrees recentes'}</h3>
        </div>
        {recentEntries.length > 0 ? (
          <table className="data-table">
            <thead><tr><th>Date</th><th>{language === 'en' ? 'Start' : 'Debut'}</th><th>{language === 'en' ? 'End' : 'Fin'}</th><th>{language === 'en' ? 'Hours' : 'Heures'}</th><th>{language === 'en' ? 'Status' : 'Statut'}</th></tr></thead>
            <tbody>
              {recentEntries.map(entry => (
                <tr key={entry.id}>
                  <td>{format(parseISO(entry.date), 'dd/MM/yyyy')}</td>
                  <td>{entry.start_time}</td>
                  <td>{entry.end_time}</td>
                  <td>{Math.round(entry.total_hours * 100) / 100}h</td>
                  <td>{getStatusBadge(entry.status)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="empty-state">
            <Clock size={48} className="empty-state-icon" />
            <p className="empty-state-text">{language === 'en' ? 'No recent entries' : 'Aucune entree recente'}</p>
          </div>
        )}
      </div>
    </div>
  );
};
